import { isObject } from 'lodash-es'
import { z } from 'zod'

import { Pagination } from '@forto-dev/shipment-models'
import { CustomerCargoDetailsResponse } from '@forto-dev/shipment-models/dist/houseShipmentModels/customer/customerCargoDetails.response'
import { CustomerFilterLookupDataResponse } from '@forto-dev/shipment-models/dist/houseShipmentModels/customer/customerFilterLookupData.response'
import { TransportScheme } from '@freighthub/freighthub-transportplanning-sdk'

import Client from '@/api/client'
import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { getConfig } from '@/app/config'

import { shipmentV2ToOverviewItem } from './shipmentToOverviewItem'
import { CostType, Currency, PriceBreakdownResponse, PriceBreakdownSchema, PriceBreakdownType } from '../domain/costs'
import { CustomsStatus } from '../domain/customs'
import { DemurrageDetentionCharges } from '../domain/demurrage-detention'
import { DetailedShipment } from '../domain/detailed-shipment'
import { Emissions } from '../domain/emissions'
import { OverviewItem, OverviewItemResponse } from '../domain/overviewItem'
import { CustomerHouseShipmentResponse } from '../types'

const client = new Client(getConfig().API_ENDPOINT ?? '')
const revenueBasePath = '/api/revenue'

export type ShipmentV2Response = {
  limit: number
  offset: number
  orderBy: string
  results: CustomerHouseShipmentResponse[]
  total: number
}

export const getShipmentsV2 = async ({ pageParam }: { pageParam: Pagination }): Promise<OverviewItemResponse> => {
  const { results, total } = await client.get<ShipmentV2Response>('/api/shipments/customer/house-shipments/v2', {
    params: {
      ...pageParam,
    },
  })
  const items: OverviewItem[] = []
  results.forEach((shipment) => {
    const overviewItem = shipmentV2ToOverviewItem(shipment)
    if (overviewItem) {
      items.push(overviewItem)
    }
  })
  return {
    items,
    total,
  }
}

export const getShipmentsLookupV2 = async (): Promise<CustomerFilterLookupDataResponse> => {
  const results = await client.get<CustomerFilterLookupDataResponse>(
    '/api/shipments/customer/house-shipments/v2/lookup'
  )
  return results
}

export const getShipmentCargoSummary = async (code: string): Promise<CustomerCargoDetailsResponse[]> => {
  const results = await client.get<CustomerCargoDetailsResponse[]>(
    `/api/shipments/customer/house-shipments/v2/${code}/cargo`
  )
  return results
}

export const getShipment = async (shipmentId: string): Promise<DetailedShipment> => {
  return await client.get<DetailedShipment>(`/api/shipping/api/shipments/${shipmentId}`)
}

export const getShipmentEmissions = async (shipmentId: string): Promise<Emissions> => {
  return await client.get<Emissions>(`/api/carbon-emissions/emissions?shipmentId=${shipmentId}`)
}

export const getShipmentDemurrageDetention = async (shipmentId: string): Promise<DemurrageDetentionCharges> => {
  return await client.get<DemurrageDetentionCharges>(`/api/dem-det/rates/selling/${shipmentId}`)
}

export const getShipmentTransportScheme = async (shipmentId: string): Promise<TransportScheme> => {
  return await client.get<TransportScheme>(`/api/shipping/api/shipments/${shipmentId}/transportScheme`)
}

export const getShipmentCustomsStatus = async (shipmentId: string): Promise<CustomsStatus> => {
  return await client.get<CustomsStatus>(`/api/customs-status/customs-status/${shipmentId}`)
}

export type UpdateShipmentInformationProps = {
  shipmentId: string
  data: Partial<DetailedShipment>
}

export const updateShipmentInformation = async (props: UpdateShipmentInformationProps): Promise<void> => {
  await client.put(`/api/shipping/api/shipments/${props.shipmentId}`, {
    ...props.data,
    additionalInformation: props.data.additionalInformation || null,
  })
}

type CurrencyMap = Record<string, number>

const sortCurrencies = (currenciesMap: CurrencyMap) => {
  return Object.entries(currenciesMap)
    .map(([currency, value]) => ({ currency, value }))
    .sort((a, b) => b.value - a.value)
}

const calculateTotalCosts = (costs: PriceBreakdownResponse) => {
  const totalCosts = {} as CurrencyMap

  const totalCostMap = costs.categories.reduce(
    (categoryAcc, category) => {
      const { lineItems, type } = category
      if (!lineItems.length) {
        return categoryAcc
      }

      const categoryTotalCost = lineItems.reduce((acc, { invoiceValue }) => {
        acc[invoiceValue.currency] = (acc[invoiceValue.currency] ?? 0) + invoiceValue.value
        totalCosts[invoiceValue.currency] = (totalCosts[invoiceValue.currency] ?? 0) + invoiceValue.value
        return acc
      }, {} as CurrencyMap)

      categoryAcc[type] = sortCurrencies(categoryTotalCost)
      return categoryAcc
    },
    {} as Record<CostType, Currency[]>
  )

  return {
    categoryCosts: totalCostMap,
    totalCosts: sortCurrencies(totalCosts),
  }
}

export const shipmentsConfig: ApiConfig<{
  getPriceBreakdowns: EndpointConfig<string, PriceBreakdownResponse, PriceBreakdownType>
}> = {
  getPriceBreakdowns: {
    requestConfig: (shipmentId: string) => ({
      endpoint: `${revenueBasePath}/price-breakdowns/${shipmentId}`,
      method: 'get',
      deserializationStrategy: z.preprocess((data) => {
        if (isObject(data)) {
          const costsWithTotalCosts = { ...data, ...calculateTotalCosts(data as PriceBreakdownResponse) }
          return costsWithTotalCosts
        }
        return {}
      }, PriceBreakdownSchema),
    }),
  },
}
export const shipmentsClient = clientFactory(shipmentsConfig)
